// Full QC result for one video: the AI reviewer's verdict and reasoning, the
// frame-by-frame timeline, per-requirement evidence, flagged issues,
// format/transcript, human review and history. While a run is going it
// shows the live progress instead (and the last result below, if any).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import DecisionBox from './DecisionBox';
import VerdictCard from './VerdictCard';
import VideoTimeline from './VideoTimeline';
import { Outliers, ResultChecklist } from './ResultChecklist';
import { Details, History } from './ResultDetails';
import { ScanPanel } from './ScanProgress';
import usePolling from './usePolling';
import { isRunning } from './qcFormat';
import { asText, errorText } from './qcClient';
import { AiMark, HowItWorks, Skeleton } from './aiUi';
import { Button, C, Empty, Modal, Pill, StatusBadge } from './ui';

const POLL_MS = 3000;

const reducedMotion = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ResultSkeleton = () => (
    <div role="status" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: C.muted }}>
            <AiMark size={30} active />Loading result…
        </div>
        <Skeleton height={170} radius={16} />
        <Skeleton height={300} radius={16} />
    </div>
);

export default function QcResultModal({ client, qcVideoId, title, sample = false, track = () => {}, onClose, onChanged }) {
    const [video, setVideo] = useState(null);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState('');
    const [rerunning, setRerunning] = useState(false);
    const [selectedT, setSelectedT] = useState(null);
    const mounted = useRef(true);
    const loadSeq = useRef(0);
    const timeline = useRef(null);

    useEffect(() => () => { mounted.current = false; }, []);

    // The video is polled while running; history loads on open and after a
    // run or a decision. A failing history call never hides the result.
    const loadVideo = useCallback(async () => {
        loadSeq.current += 1;
        const seq = loadSeq.current;
        try {
            const v = await client.getVideo(qcVideoId);
            if (mounted.current && seq === loadSeq.current) { setVideo(v); setError(''); }
        } catch (err) {
            if (mounted.current && seq === loadSeq.current) setError(errorText(err));
        }
    }, [client, qcVideoId]);

    const loadHistory = useCallback(async () => {
        try {
            const h = await client.getHistory(qcVideoId);
            if (mounted.current) setHistory(Array.isArray(h) ? h : []);
        } catch (err) {
            if (mounted.current) setHistory([]);
        }
    }, [client, qcVideoId]);

    useEffect(() => { loadVideo(); loadHistory(); }, [loadVideo, loadHistory]);

    const running = Boolean(video) && isRunning(video.status);
    const wasRunning = useRef(false);
    useEffect(() => {
        if (wasRunning.current && !running) { loadHistory(); setSelectedT(null); }
        wasRunning.current = running;
    }, [running, loadHistory]);
    usePolling(loadVideo, running, POLL_MS);

    const rerun = async () => {
        setRerunning(true);
        try {
            await client.runQc(qcVideoId);
            track('video_qc_run', { rerun: true, from: 'result' });
            onChanged();
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            if (mounted.current) setRerunning(false);
            await loadVideo();
        }
    };

    // A timestamp anywhere in the result: show that frame on the timeline.
    const jumpTo = (t) => {
        setSelectedT(t);
        const el = timeline.current;
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    };

    const afterDecision = () => { loadVideo(); loadHistory(); onChanged(); };
    const check = video?.latest_check;

    return (
        <Modal title={asText(video?.title) || title} subtitle={asText(video?.source)} onClose={onClose} width={1000}
            badge={sample ? <Pill bg="#fef3c7" color="#92400e">Sample data</Pill> : null}
            footer={(
                <Button onClick={rerun} disabled={!video || running || rerunning}>
                    <ReplayRoundedIcon aria-hidden="true" style={{ fontSize: 16 }} />{rerunning ? 'Starting…' : 'Re-run QC'}
                </Button>
            )}>
            {!video && !error && <ResultSkeleton />}
            {error && (
                <div role="alert" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, color: '#991b1b', background: '#fee2e2', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: video ? 16 : 0 }}>
                    <span>{error}</span>
                    <Button small onClick={loadVideo}>Retry</Button>
                </div>
            )}
            {video && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
                    {running && <ScanPanel status={video.status} stage={video.stage} hasPrevious={Boolean(check)} />}
                    {video.status === 'error' && (
                        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px', fontSize: 13 }}>
                            <StatusBadge status="error" /> {asText(video.error, 'The last run failed.')}
                        </div>
                    )}
                    {check ? (
                        <>
                            <VerdictCard check={check} />
                            <VideoTimeline ref={timeline} video={video} check={check} selectedT={selectedT} onSelect={setSelectedT} />
                            <ResultChecklist results={check.results} onJump={jumpTo} />
                            <Outliers outliers={check.outliers} onJump={jumpTo} />
                            <Details video={video} onJump={jumpTo} />
                            <DecisionBox videoId={video.id} check={check} client={client} sample={sample} track={track} onDecided={afterDecision} />
                            <History checks={history} />
                        </>
                    ) : (
                        !running && video.status !== 'error' && (
                            <Empty icon={<AiMark size={44} />} title="No result yet" extra={<HowItWorks style={{ justifyContent: 'center', marginTop: 14 }} />}>
                                Run QC and the AI reviewer will check this video against the brief.
                            </Empty>
                        )
                    )}
                </div>
            )}
        </Modal>
    );
}
